import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { type LucideIcon, LoaderCircle, Trash2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

interface DeleteModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  onDelete: () => Promise<void>
  loading: boolean
  title?: string
  description?: string
  buttonLabel?: string
  loadingButtonLabel?: string
  successMessageDescription?: string
  successMessageTitle?: string
  isNested?: boolean
  headerIcon?: LucideIcon
  loadingIcon?: LucideIcon
  confirmIcon?: LucideIcon
  className?: string
  hideToast?: boolean
}

const DeleteModal: React.FC<DeleteModalProps> = ({
  open,
  setOpen,
  onDelete,
  loading,
  title = 'Delete Item',
  description = 'Are you sure you want to delete this item?',
  buttonLabel = 'Delete',
  loadingButtonLabel = 'Deleting...',
  successMessageDescription,
  successMessageTitle,
  isNested = false,
  headerIcon: HeaderIcon = Trash2,
  loadingIcon: LoadingIcon = LoaderCircle,
  confirmIcon: ConfirmIcon = Trash2,
  hideToast = false,
  className,
}) => {
  const [confirm, setConfirm] = useState(false)

  // Memoize the onDelete callback to prevent infinite re-renders
  const memoizedOnDelete = useMemo(() => onDelete, [onDelete])

  useEffect(() => {
    if (confirm) {
      void (async () => {
        try {
          await memoizedOnDelete()

          if (!hideToast) {
            toast.success(successMessageTitle ? successMessageTitle : 'Deleted Successfully', {
              description: successMessageDescription ? successMessageDescription : undefined,
            })
          }
        } catch (err: unknown) {
          toast.error('Error', {
            description: err instanceof Error ? err?.message : 'Something went wrong',
          })
        } finally {
          setConfirm(false)
          setOpen(false)
        }
      })()
    }
  }, [confirm])

  // Keyboard shortcuts: Cmd+Enter confirms, Esc closes.
  // Registered on window in the capture phase so it runs before parent dialogs
  // (e.g. Radix) and stops them from also handling the same key.
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (!loading && !confirm) setOpen(false)
        return
      }

      if (e.key === 'Enter' && e.metaKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (!loading && !confirm) setConfirm(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [open, loading, confirm, setOpen])

  if (!open) return null

  return (
    <div
      className={cn(
        `fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-in fade-in duration-300`,
        isNested ? 'bg-black/40 backdrop-blur-none' : 'bg-black/60 backdrop-blur-sm',
        className
      )}
    >
      <div className='flex flex-col max-w-[420px] w-full bg-white dark:bg-card rounded-lg shadow-xl border relative overflow-hidden animate-in zoom-in-95 duration-300'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b dark:border-gray-700'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center'>
              <HeaderIcon className='w-5 h-5 text-red-600 dark:text-red-400' />
            </div>
            <h2 className='text-lg  text-gray-900 dark:text-white'>{title || 'Confirm Delete'}</h2>
          </div>

          <button
            type='button'
            className='w-8 h-8 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors duration-200'
            onClick={() => setOpen(false)}
          >
            <X className='w-4 h-4 text-gray-500 dark:text-gray-400' />
          </button>
        </div>

        {/* Content */}
        <div className='p-6'>
          <p className='text-gray-700 text-left dark:text-gray-300 mb-2'>
            {description || 'Are you sure you want to delete this item?'}
          </p>

          <div className='mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg'>
            <p className='text-sm text-amber-800 dark:text-amber-200 font-medium flex items-center gap-2'>
              <span className='w-1.5 h-1.5 bg-amber-500 rounded-full'></span>
              This action cannot be undone.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className='px-6 pb-6 flex gap-3 justify-end border-t pt-4'>
          <Button variant='outline' onClick={() => setOpen(false)} disabled={loading}>
            <X />
            Cancel
          </Button>
          <Button disabled={loading} onClick={() => setConfirm(true)} variant='destructive'>
            {loading ? <LoadingIcon className='animate-spin' /> : <ConfirmIcon />}
            {loading ? loadingButtonLabel : buttonLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DeleteModal
